import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../atoms/Icon';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/authStore';
import { useSavedNames } from '../saved/savedListUtils';

/**
 * MobileBottomNav organism - Bottom tab navigation for mobile
 * Brand Design: Black background with amber accents - matches header for unified look
 */
export function MobileBottomNav({ currentPage, className }: { currentPage?: string; className?: string }) {
  const { token } = useAuthStore();
  const location = useLocation();
  const savedCount = useSavedNames().length;

  // R2-6 (2026-07-07) — 4 tabs for a vibe-first app. Home IS the vibe entry
  // (the landing textbox → /vibe conversation), so the old Search/Vibe/Map
  // triplet collapsed: Criteria lives behind home links + the top nav.
  const navItems = [
    { id: 'home', label: 'Home', icon: 'message-circle', to: '/' },
    { id: 'explore', label: 'Map', icon: 'map-pin', to: '/browse' },
    { id: 'saved', label: 'Saved', icon: 'heart', to: '/my-list', badge: savedCount },
    { id: 'profile', label: 'Profile', icon: 'user', to: token ? '/dashboard' : '/profile' },
  ];

  // Determine active tab from currentPage prop or URL
  const activeTab = (() => {
    // Legacy currentPage values from the 5-tab era map onto the new set.
    if (currentPage === 'vibe' || currentPage === 'home') return 'home';
    if (currentPage === 'search') return '';
    if (currentPage) return currentPage;
    const path = location.pathname;
    if (path === '/' || path.startsWith('/vibe')) return 'home';
    if (path.startsWith('/explore') || path.startsWith('/browse')) return 'explore';
    if (path.startsWith('/my-list')) return 'saved';
    if (path.startsWith('/dashboard') || path.startsWith('/login') || path.startsWith('/profile')) return 'profile';
    return '';
  })();

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-50',
        'border-t md:hidden',
        className
      )}
      style={{
        background: 'var(--ts-anchor)',
        borderColor: 'var(--ts-anchor-border)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="mobile-bottom-nav"
    >
      <div className="flex items-center justify-around h-16 relative">
        {navItems.map((item) => {
          const isActive = activeTab === item.id;
          return (
            <Link
              key={item.id}
              to={item.to}
              className={cn(
                'relative flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 min-w-[64px] min-h-[48px]',
                'transition-all duration-200',
                'active:scale-90',
              )}
              style={isActive ? { color: 'var(--ts-accent-primary)' } : { color: 'var(--ts-anchor-text-muted)' }}
              data-testid={`mobile-nav-${item.id}`}
            >
              {/* T11 — saved = gold everywhere, including this count. */}
              {(item.badge ?? 0) > 0 && (
                <span
                  className="absolute top-0 right-2 min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold px-1"
                  style={{ background: 'var(--ts-accent-primary)', color: 'var(--ts-accent-contrast)', boxShadow: '0 0 10px var(--ts-accent-glow)' }}
                  data-testid="mobile-saved-count-badge"
                >
                  {item.badge}
                </span>
              )}

              {/* Active indicator dot */}
              {isActive && (
                <span
                  className="absolute -top-0.5 w-1.5 h-1.5 rounded-full animate-in fade-in zoom-in duration-300"
                  style={{ background: 'var(--ts-accent-primary)' }}
                  data-testid={`mobile-nav-active-${item.id}`}
                />
              )}

              <span
                className={cn(
                  'transition-transform duration-200',
                  isActive ? 'scale-110' : 'scale-100'
                )}
                style={isActive ? { filter: 'drop-shadow(0 0 8px var(--ts-accent-glow))' } : undefined}
              >
                {/* DESIGN.md — 🌮 is the save affordance, bottom-nav Saved
                    included. Everything else is a lucide Icon. */}
                {item.id === 'saved'
                  ? <span aria-hidden className="block text-[20px] leading-none">🌮</span>
                  : (
                    <Icon
                      name={item.icon}
                      size="md"
                      className={cn(
                        'transition-colors duration-200',
                        isActive && 'stroke-[2.5]'
                      )}
                    />
                  )}
              </span>

              <span
                className="text-[11px] font-semibold transition-all duration-200"
                style={{
                  fontFamily: 'var(--ts-font-ui)',
                  color: isActive ? 'var(--ts-accent-primary)' : 'var(--ts-anchor-text-muted)',
                  textShadow: isActive ? '0 0 8px var(--ts-accent-glow)' : 'none',
                }}
              >
                {item.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
